import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGetSchoolTeachersQuery } from '../../store/api/schoolAdminApi'
import { useSchoolFilters } from '../../components/school/SchoolLayout'
import DateRangeBar from '../../components/school/DateRangeBar'
import { UsageBadge, formatDate, EmptyState, DemoBanner } from '../../components/school/SchoolUi'

const SchoolTeachers: React.FC = () => {
  const { t } = useTranslation('school')
  const { filters } = useSchoolFilters()
  const { data, isLoading } = useGetSchoolTeachersQuery(filters)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('teachers.title')}</h1>
        <p className="text-gray-600">{t('teachers.subtitle')}</p>
      </div>
      {data?.isDemo ? <DemoBanner label={t('common.demoBanner')} /> : null}
      <DateRangeBar showClassFilter={false} />
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-gray-500">{t('common.loading')}</div>
        ) : !(data?.teachers || []).length ? (
          <EmptyState title={t('teachers.empty')} hint={t('teachers.emptyHint')} />
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.teacher')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.classes')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.activities')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.activeStudents')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.aiSessions')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.lastActivity')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('teachers.columns.usage')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.teachers.map((row: any) => (
                <tr key={row.userId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <Link to={`/pending/teachers/${row.userId}`} className="text-blue-600 hover:underline font-medium">{row.name}</Link>
                    <div className="text-xs text-gray-500">{row.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{row.classCount}</td>
                  <td className="px-4 py-3 text-sm">{row.activities}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {row.activeStudents}
                    <span className="text-gray-400 font-normal"> / {row.totalStudents ?? '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">{row.aiSessions}</td>
                  <td className="px-4 py-3 text-sm">{formatDate(row.lastActivity)}</td>
                  <td className="px-4 py-3 text-sm">
                    <UsageBadge status={row.usageStatus} label={t(`teachers.usage.${row.usageStatus}`)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default SchoolTeachers
